import type { ProductListType } from "src/types/product-listing";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/macro";
import { localeFixOptional } from "src/utils/genderLocale/genderLocale";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";

type PlpTitleParams = {
  i18n: I18n;
  productListType: ProductListType;
  title?: string;
  gender?: string | null;
};

const GENDER_IN_PAGE_TITLE: ProductListType[] = [
  "CATEGORY_PLP",
  "BRAND_PLP",
  "SHOP_TYPE_PLP",
];
const GENDER_IN_HEADING: ProductListType[] = ["BRAND_PLP"];

const getGenderLabel = (
  { i18n, productListType, title, gender }: PlpTitleParams,
  plpTypes: ProductListType[]
): string | undefined => {
  const shopType = localeFixOptional({ gender });
  if (!shopType || !plpTypes.includes(productListType)) return undefined;

  const label = i18n._(shopTypeTranslation[shopType]);
  return label === title ? undefined : label;
};

/**
 * Builds a unique, translatable <title> for each PLP type.
 */
const buildPlpPageTitle = (params: PlpTitleParams): string => {
  const { i18n, productListType, title } = params;
  const prefix = productListType === "SEARCH_PLP" ? i18n._(msg`Search`) : "";
  const genderLabel = getGenderLabel(params, GENDER_IN_PAGE_TITLE);

  const pageTitle = [prefix, title, genderLabel].filter(Boolean).join(" ");
  return pageTitle
    ? `${pageTitle} | Otrium`
    : i18n._(msg`Online Outlet | Otrium`);
};

/** Gendered brand PLPs append the shop type so each variant gets a unique <h1>. */
const buildPlpHeading = (params: PlpTitleParams): string | undefined => {
  const genderLabel = getGenderLabel(params, GENDER_IN_HEADING);
  return params.title && genderLabel
    ? `${params.title} | ${genderLabel}`
    : params.title;
};

export { buildPlpPageTitle, buildPlpHeading };
